import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Instagram, Twitter, Linkedin, Youtube, Facebook, MessageCircle, Phone } from "lucide-react";
import { MaskedLines, FadeUp } from "@/components/Reveal";
import FAQ from "@/components/FAQ";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICE_NAMES, ORG_TYPES, BUDGETS, INDIAN_STATES, CONTACT, PARTY_OPTIONS } from "@/data/services";
import useSeo from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
const inputCls = "mt-3 border-white/15 bg-coal/60 text-paper placeholder:text-zinc-600 focus-visible:ring-brand";
const labelCls = "font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500";

const EMPTY = { name: "", phone: "", email: "", company: "", org_type: "", party: "", state: "Madhya Pradesh", service: "", budget: "", message: "" };

const SOCIALS = [
  { icon: Instagram, label: "Instagram" },
  { icon: Facebook, label: "Facebook" },
  { icon: Twitter, label: "X" },
  { icon: Youtube, label: "YouTube" },
  { icon: Linkedin, label: "LinkedIn" },
];

function Field({ label, htmlFor, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

function Dropdown({ value, onChange, options, placeholder, testId, prefix }) {
  return (
    <div className="mt-3">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className="border-white/15 bg-coal/60 text-paper focus:ring-brand">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-72 border-white/10 bg-coal text-paper">
          {options.map((o) => (
            <SelectItem key={o} value={o} data-testid={`${prefix}-${slug(o)}`}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function Contact() {
  useSeo("Contact — Plan a Campaign", "Call or WhatsApp +91 99199 99647. Office: Scheme 54, Vijay Nagar, Indore. Tell us your seat, date and challenge.");
  const [params] = useSearchParams();
  const pkg = params.get("package");
  const svcParam = params.get("service");
  const [form, setForm] = useState(() =>
    svcParam
      ? { ...EMPTY, service: SERVICE_NAMES.includes(svcParam) ? svcParam : "" }
      : pkg
      ? {
          ...EMPTY,
          service: /reels/i.test(pkg) ? "Creatives" : /brand/i.test(pkg) ? "Branding" : "Election Campaigns",
          message: `I am interested in the ${pkg} package. `,
        }
      : EMPTY
  );
  const [sending, setSending] = useState(false);
  const [time, setTime] = useState("");

  useEffect(() => {
    const tick = () =>
      setTime(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" }));
    tick();
    const i = setInterval(tick, 10000);
    return () => clearInterval(i);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pick = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const phoneOk = /^(\+91[\s-]?)?[6-9]\d{4}[\s-]?\d{5}$/.test(form.phone.trim());
    if (!form.name.trim() || !form.email.trim() || !form.service || !form.message.trim()) {
      toast.error("Please fill in name, email, mobile, service and campaign details.");
      return;
    }
    if (!phoneOk) {
      toast.error("Please enter a valid 10-digit Indian mobile number.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        phone: form.phone.trim(),
        company: form.company || null,
        org_type: form.org_type || null,
        party: form.party || null,
        state: form.state || null,
        budget: form.budget || null,
      });
      toast.success("Enquiry received. Our Indore team will call you within 24 hours.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again or WhatsApp us.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="mx-auto max-w-[1440px] px-6 pb-24 pt-36 lg:px-12 lg:pt-44">
      <p className="font-mono text-xs font-medium uppercase tracking-[0.25em] text-saffron">Contact — Indore, Madhya Pradesh</p>
      <h1 className="mt-5 font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight text-paper sm:text-6xl lg:text-7xl">
        <MaskedLines inView={false} delay={0.1} lines={["Plan your", <span key="p" className="ribbon-text font-hindi normal-case tracking-normal">अभियान.</span>]} />
      </h1>

      <div className="mt-16 grid grid-cols-1 gap-16 lg:grid-cols-12">
        <FadeUp delay={0.2} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field label="Name *" htmlFor="contact-name">
                <Input id="contact-name" data-testid="contact-name-input" value={form.name} onChange={set("name")} placeholder="Rahul Sharma" className={inputCls} />
              </Field>
              <Field label="Mobile (WhatsApp) *" htmlFor="contact-phone">
                <Input id="contact-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 98XXX XXXXX" className={inputCls} />
              </Field>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field label="Email *" htmlFor="contact-email">
                <Input id="contact-email" data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder="rahul@party.in" className={inputCls} />
              </Field>
              <Field label="Organisation / Party / Brand" htmlFor="contact-company">
                <Input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Organisation name" className={inputCls} />
              </Field>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field label="You are a">
                <Dropdown value={form.org_type} onChange={pick("org_type")} options={ORG_TYPES} placeholder="Select type" testId="contact-orgtype-select" prefix="orgtype-option" />
              </Field>
              <Field label="State">
                <Dropdown value={form.state} onChange={pick("state")} options={INDIAN_STATES} placeholder="Select state" testId="contact-state-select" prefix="state-option" />
              </Field>
            </div>

            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <Field label="Party (if political)">
                <Dropdown value={form.party} onChange={pick("party")} options={PARTY_OPTIONS} placeholder="Select party" testId="contact-party-select" prefix="party-option" />
              </Field>
              <Field label="What do you need? *">
                <Dropdown value={form.service} onChange={pick("service")} options={SERVICE_NAMES} placeholder="Select a service" testId="contact-service-select" prefix="service-option" />
              </Field>
            </div>


            <div>
              <label className={labelCls}>Budget range</label>
              <div className="mt-3 flex flex-wrap gap-2" data-testid="contact-budget-group">
                {BUDGETS.map((b) => (
                  <button
                    type="button"
                    key={b.id}
                    data-testid={`budget-${b.id}`}
                    onClick={() => setForm((f) => ({ ...f, budget: f.budget === b.label ? "" : b.label }))}
                    className={`rounded-full border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.12em] transition-colors duration-300 ${
                      form.budget === b.label
                        ? "border-brand bg-brand text-white"
                        : "border-white/15 text-zinc-400 hover:border-brand/50 hover:text-paper"
                    }`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>

            <Field label="Campaign / project details *" htmlFor="contact-message">
              <Textarea
                id="contact-message"
                data-testid="contact-message-input"
                value={form.message}
                onChange={set("message")}
                placeholder="Seat or constituency, election date, current challenge — or your brand / reels brief."
                rows={6}
                className={inputCls}
              />
            </Field>

            <div className="flex flex-wrap items-center gap-4">
              <button
                type="submit"
                data-testid="submit-enquiry-button"
                disabled={sending}
                className="group inline-flex items-center gap-2 rounded-full btn-ribbon px-9 py-4 font-mono text-xs font-medium uppercase tracking-[0.15em] text-white transition-colors duration-300  disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send enquiry"}
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-button"
                className="group inline-flex items-center gap-2 rounded-full border border-[#25D366]/50 px-7 py-4 font-mono text-xs font-medium uppercase tracking-[0.15em] text-[#25D366] transition-colors duration-300 hover:bg-[#25D366] hover:text-ink"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp us
              </a>
            </div>
          </form>
        </FadeUp>

        <FadeUp delay={0.3} className="lg:col-span-5">
          <div className="space-y-10 lg:pl-10">
            <div>
              <p className={labelCls}>Call / WhatsApp</p>
              <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="mt-3 flex items-center gap-3 font-display text-xl font-bold text-paper transition-colors duration-300 hover:text-saffron sm:text-2xl">
                <Phone className="h-5 w-5 text-saffron" />
                {CONTACT.phone}
              </a>
            </div>
            <div>
              <p className={labelCls}>New business</p>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="mt-3 block font-display text-xl font-bold text-paper transition-colors duration-300 hover:text-brand sm:text-2xl">
                {CONTACT.email}
              </a>
            </div>
            <div>
              <p className={labelCls}>Office</p>
              {CONTACT.address.map((l) => (
                <p key={l} className="mt-1 text-base text-zinc-300 first:mt-3">{l}</p>
              ))}
              <p className="mt-1 text-sm text-zinc-500">Field teams across Madhya Pradesh · Campaigns across India</p>
            </div>
            <div>
              <p className={labelCls}>Indore time (IST)</p>
              <p className="mt-3 font-mono text-lg text-paper" data-testid="contact-local-time">{time}</p>
            </div>
            <div className="flex gap-3">
              {SOCIALS.map(({ icon: Icon, label }) => (
                <a
                  key={label}
                  href="#"
                  aria-label={label}
                  data-testid={`contact-social-${label.toLowerCase()}`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-zinc-400 transition-colors duration-300 hover:border-saffron hover:text-saffron"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </FadeUp>
      </div>

      <FAQ className="mt-24 lg:mt-32" />
    </div>
  );
}
